import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Pause,
  Play,
  StopCircle,
  Trash2,
  RotateCcw,
  RotateCw,
  VolumeX,
} from "lucide-react";

import {
  AudioEditUtils,
  AudioSelection,
  HistoryState,
  useAudioEdit,
} from "./useAudioEdit";
import "./SingleAudioEditor.css";
import { formatTime } from "../../module/audio/audio.util";
import { useThrottle } from "../../hooks/useThrottle";

const TRACK_OFFSET = {
  MIN: -5, // 최소 -5초
  MAX: 5, // 최대 5초
  DRAG_SENSITIVITY: 1.5, // 드래그 감도 (높을수록 덜 민감)
};

interface SingleAudioEditorProps {
  audioBuffer: AudioBuffer | null;
  setAudioBuffer: (buffer: AudioBuffer) => void;
  audioContext: AudioContext;
  gainNode: GainNode;
  isPlaying: boolean;
  currentTime: number;
  zoom: number;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (time: number) => void;
  onDelete: (selection: AudioSelection) => Promise<void>;
  timeOffset: number; // 새로운 prop
  onTimeOffsetChange: (offset: number) => void;
}

type EditorMode = "playback" | "edit";

const SingleAudioEditor: React.FC<SingleAudioEditorProps> = ({
  audioBuffer,
  setAudioBuffer,
  audioContext,
  gainNode,
  isPlaying,
  currentTime,
  onPlay,
  onPause,
  onStop,
  onSeek,
  onDelete,
  zoom,
  timeOffset,
  onTimeOffsetChange,
}) => {
  const [selection, setSelection] = useState<AudioSelection>({
    start: null,
    end: null,
  });
  const [editorMode, setEditorMode] = useState<EditorMode>("edit");
  const [isDragging, setIsDragging] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(800);

  const [isPlayheadDragging, setIsPlayheadDragging] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const playheadRef = useRef<HTMLDivElement>(null);
  const lastPlayTimeRef = useRef<number>(0); // 마지막 재생 시점 저장용

  const [historyState, setHistoryState] = useState<HistoryState>({
    canUndo: false,
    canRedo: false,
  });
  const [isTrackDragging, setIsTrackDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [initialOffset, setInitialOffset] = useState(0);
  const [isDraggingWithAlt, setIsDraggingWithAlt] = useState(false);

  const [waveformCache, setWaveformCache] = useState<{
    [key: string]: {
      min: Float32Array;
      max: Float32Array;
      width: number;
    };
  }>({});

  // 웨이브폼 데이터 계산 함수
  const calculateWaveformData = useCallback(
    (channelData: Float32Array, width: number, zoom: number) => {
      // audioBuffer를 식별할 수 있는 값 추가 (예: 길이나 첫 몇 개 샘플의 해시)
      const bufferSignature = channelData.length + "-" + channelData[0];
      const cacheKey = `${bufferSignature}-${width}-${zoom}`;

      if (waveformCache[cacheKey]) {
        return waveformCache[cacheKey];
      }

      const samplesPerPixel = Math.floor(channelData.length / width);
      const dataPoints = Math.ceil(width);
      const minValues = new Float32Array(dataPoints);
      const maxValues = new Float32Array(dataPoints);

      for (let i = 0; i < dataPoints; i++) {
        let min = 1.0;
        let max = -1.0;
        const startSample = Math.floor((i / width) * channelData.length);
        const endSample = Math.min(
          startSample + samplesPerPixel,
          channelData.length
        );

        for (let j = startSample; j < endSample; j++) {
          const sample = channelData[j];
          if (sample < min) min = sample;
          if (sample > max) max = sample;
        }

        minValues[i] = min;
        maxValues[i] = max;
      }

      const newCache = {
        min: minValues,
        max: maxValues,
        width,
      };

      setWaveformCache((prev) => ({
        ...prev,
        [cacheKey]: newCache,
      }));

      return newCache;
    },
    []
  );

  useEffect(() => {
    setWaveformCache({});
  }, [audioBuffer]);

  // drawWaveform 함수 수정
  const drawWaveform = useCallback(() => {
    if (!canvasRef.current || !audioBuffer) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const { width, height } = canvas;

    // 배경 지우기
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(0, 0, width, height);

    const channelData = audioBuffer.getChannelData(0);
    const amp = height / 2;

    // 캐시된 웨이브폼 데이터 사용
    const cacheKey = `${width}-${zoom}`;
    const waveformData =
      waveformCache[cacheKey] ||
      calculateWaveformData(channelData, width, zoom);

    // 오프셋을 픽셀 단위로 변환
    const offsetPixels = (timeOffset / audioBuffer.duration) * width;

    // 파형 그리기
    ctx.beginPath();
    ctx.strokeStyle = "#4a9eff";
    ctx.lineWidth = 1;

    const gainValue = gainNode.gain.value;

    for (let i = 0; i < width; i++) {
      // 수정된 부분: offsetPixels를 더해서 파형을 이동
      const dataIndex = Math.floor(i - offsetPixels);
      if (dataIndex < 0 || dataIndex >= waveformData.min.length) continue;

      const min = waveformData.min[dataIndex] * gainValue;
      const max = waveformData.max[dataIndex] * gainValue;

      ctx.moveTo(i, (1 + min) * amp);
      ctx.lineTo(i, (1 + max) * amp);
    }

    ctx.stroke();

    // Selection 영역 그리기
    if (selection.start !== null && selection.end !== null) {
      const startX = (selection.start / audioBuffer.duration) * width; // 오프셋 제거
      const endX = (selection.end / audioBuffer.duration) * width; // 오프셋 제거

      ctx.fillStyle = "rgba(74, 158, 255, 0.3)";
      ctx.fillRect(startX, 0, endX - startX, height);

      ctx.strokeStyle = "rgba(74, 158, 255, 0.8)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(startX, 0);
      ctx.lineTo(startX, height);
      ctx.moveTo(endX, 0);
      ctx.lineTo(endX, height);
      ctx.stroke();
    }

    // 중앙 가이드라인 그리기
    if (timeOffset !== 0) {
      const centerX = width / 2;
      ctx.strokeStyle = "rgba(255, 165, 0, 0.5)";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(centerX, 0);
      ctx.lineTo(centerX, height);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [
    audioBuffer,
    selection,
    timeOffset,
    zoom,
    gainNode.gain.value,
    calculateWaveformData,
    waveformCache,
  ]);
  // Throttled drawWaveform
  const throttledDraw = useThrottle(drawWaveform, 16);

  useEffect(() => {
    throttledDraw();
  }, [throttledDraw]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) {
        containerRef.current?.classList.add("alt-drag-mode");
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.altKey) {
        containerRef.current?.classList.remove("alt-drag-mode");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const generateTimeMarkers = useCallback(() => {
    if (!audioBuffer) return [];

    const duration = audioBuffer.duration;
    const markers = [];
    let interval = 5; // 기본 5초 간격

    // 오디오 길이와 줌 레벨에 따라 간격 조정
    if (duration > 60) interval = 15;
    if (duration > 180) interval = 30;

    for (let time = 0; time <= duration; time += interval) {
      markers.push({
        time,
        position: (time / duration) * 100,
      });
    }

    // 보이는 영역 내의 마커만 반환
    return markers.filter((marker) => marker.position <= 100);
  }, [audioBuffer]);

  // 플레이헤드 드래그 시작
  const handlePlayheadMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // 타임라인 영역에서만 드래그 가능하도록 체크
      const target = e.target as HTMLElement;
      if (!target.closest(".timeline-area")) return;

      e.stopPropagation();
      setIsPlayheadDragging(true);

      if (isPlaying) {
        onPause();
      }
    },
    [isPlaying, onPause]
  );

  // 플레이헤드 드래그 중
  const handlePlayheadDrag = useCallback(
    (e: MouseEvent) => {
      if (!isPlayheadDragging || !containerRef.current || !audioBuffer) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const time = (x / rect.width) * audioBuffer.duration;

      onSeek(time);
    },
    [isPlayheadDragging, audioBuffer, onSeek]
  );

  // 플레이헤드 드래그 종료
  const handlePlayheadDragEnd = useCallback(() => {
    if (!isPlayheadDragging) return;
    setIsPlayheadDragging(false);
  }, [isPlayheadDragging]);

  useEffect(() => {
    if (isPlayheadDragging) {
      window.addEventListener("mousemove", handlePlayheadDrag);
      window.addEventListener("mouseup", handlePlayheadDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handlePlayheadDrag);
      window.removeEventListener("mouseup", handlePlayheadDragEnd);
    };
  }, [isPlayheadDragging, handlePlayheadDrag, handlePlayheadDragEnd]);

  const { initializeHistory, addToHistory, undo, redo, getHistoryState } =
    useAudioEdit(audioContext);

  useEffect(() => {
    if (audioBuffer) {
      initializeHistory(audioBuffer);
    }
  }, [audioBuffer, initializeHistory]);

  const updateHistoryState = useCallback(() => {
    const state = getHistoryState();
    setHistoryState(state);
  }, [getHistoryState]);

  // 편집 동작 후 히스토리 상태 업데이트
  useEffect(() => {
    updateHistoryState();
  }, [audioBuffer, updateHistoryState]);

  const handleMute = useCallback(async () => {
    if (!audioBuffer || !selection.start || !selection.end) return;

    const wasPlaying = isPlaying;
    if (wasPlaying) {
      onPause();
    }

    // 현재 버퍼의 복사본을 원본으로 저장
    const originalBuffer = audioBuffer;

    const newBuffer = await AudioEditUtils.muteSection(
      audioBuffer,
      selection.start,
      selection.end,
      audioContext
    );

    addToHistory(newBuffer, { start: null, end: null }, "mute", originalBuffer);

    setAudioBuffer(newBuffer);
    updateHistoryState();
    setSelection({ start: null, end: null });

    if (wasPlaying) {
      setTimeout(() => onPlay(), 0);
    }
  }, [audioBuffer, selection, audioContext, addToHistory, isPlaying]);

  const handleUndo = useCallback(async () => {
    const wasPlaying = isPlaying;
    if (wasPlaying) {
      onPause();
    }

    const historyItem = undo();
    if (historyItem) {
      setAudioBuffer(historyItem.buffer);
      setSelection(historyItem.selection);
      updateHistoryState();
    }

    if (wasPlaying) {
      setTimeout(() => onPlay(), 0);
    }
  }, [isPlaying, onPause, onPlay, setAudioBuffer, undo]);

  const handleRedo = useCallback(async () => {
    const wasPlaying = isPlaying;
    if (wasPlaying) {
      onPause();
    }

    const historyItem = redo();
    if (historyItem) {
      setAudioBuffer(historyItem.buffer);
      setSelection(historyItem.selection);
      updateHistoryState();
    }

    if (wasPlaying) {
      setTimeout(() => onPlay(), 0);
    }
  }, [isPlaying, onPause, onPlay, setAudioBuffer, redo]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const updateCanvasWidth = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        // canvas의 width 속성을 실제 표시되는 너비와 일치시킴
        canvasRef.current.width = rect.width;
        setCanvasWidth(rect.width);
        // 캔버스 크기가 변경되면 파형을 다시 그림
        drawWaveform();
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      // RAF를 사용하여 리사이즈 중복 호출 방지
      requestAnimationFrame(updateCanvasWidth);
    });
    resizeObserver.observe(canvasRef.current);

    // 초기 크기 설정
    updateCanvasWidth();

    return () => resizeObserver.disconnect();
  }, [drawWaveform]);

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!audioBuffer || isPlayheadDragging) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const time = (x / rect.width) * audioBuffer.duration;
      onSeek(time);
    },
    [audioBuffer, isPlayheadDragging, onSeek]
  );

  const handleDelete = useCallback(async () => {
    if (!audioBuffer || !selection.start || !selection.end) return;

    const wasPlaying = isPlaying;
    if (wasPlaying) {
      onPause();
    }

    await onDelete(selection);
    addToHistory(audioBuffer, { start: null, end: null }); // 히스토리에 추가
    updateHistoryState(); // 히스토리 상태 업데이트
    setSelection({ start: null, end: null });
  }, [audioBuffer, selection, audioContext, onStop]);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current || !audioBuffer) return;

      if (e.altKey) {
        // Alt + 드래그로 전체 트랙 이동 모드
        setIsDraggingWithAlt(true);
        setDragStartX(e.clientX);
        setInitialOffset(timeOffset);
        return;
      }

      // 기존의 selection 로직
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const time = (x / rect.width) * audioBuffer.duration;

      if (editorMode === "edit") {
        setIsDragging(true);
        setSelection({ start: time, end: time });
      } else {
        onSeek(time);
      }
    },
    [audioBuffer, editorMode, timeOffset]
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!canvasRef.current || !audioBuffer) return;

      if (isDraggingWithAlt) {
        const deltaX = e.clientX - dragStartX;
        const rect = canvasRef.current.getBoundingClientRect();
        const timeDelta = (deltaX / rect.width) * audioBuffer.duration;
        const newOffset = initialOffset + timeDelta;

        // 최소 -1초, 최대 1초로 제한
        const clampedOffset = Math.max(-1, Math.min(1, newOffset));
        onTimeOffsetChange(clampedOffset);
        return;
      }

      // 기존의 selection 드래그 로직
      if (!isDragging || editorMode !== "edit") return;
      if (!selection.start) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const time = (x / rect.width) * audioBuffer.duration;

      setSelection((prev) => ({ ...prev, end: time }));
    },
    [
      audioBuffer,
      isDragging,
      isDraggingWithAlt,
      dragStartX,
      initialOffset,
      editorMode,
    ]
  );

  const handleCanvasMouseUp = useCallback(() => {
    setIsDraggingWithAlt(false);
    setIsDragging(false);

    if (selection.start !== null && selection.end !== null) {
      if (selection.start > selection.end) {
        setSelection({
          start: selection.end,
          end: selection.start,
        });
      }
    }
  }, [selection]);

  // window에 mouseup 이벤트 리스너 추가
  useEffect(() => {
    const handleWindowMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mouseup", handleWindowMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, []);

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (editorMode !== "playback") return;
      if (!canvasRef.current || !audioBuffer) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const time = (x / rect.width) * audioBuffer.duration;

      onSeek(time);
    },
    [audioBuffer, isPlaying, onPause, onPlay, editorMode]
  );

  const isTimelineInteractive = audioBuffer != null && !isDragging;

  // 타임라인 className 동적 생성
  const getTimelineClassName = useCallback(() => {
    const baseClass = "timeline-area";
    if (isPlayheadDragging) return `${baseClass} dragging`;
    if (isTimelineInteractive) return `${baseClass} interactive`;
    return baseClass;
  }, [isPlayheadDragging, isTimelineInteractive]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .alt-drag-mode {
        cursor: ew-resize !important;
        user-select: none;
      }
      
      .alt-drag-mode .waveform-canvas {
        opacity: 0.8;
        transition: opacity 0.2s;
      }
      
      .track-offset-indicator {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: rgba(255, 165, 0, 0.9);
        color: white;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: bold;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        z-index: 1000;
      }
      
      .alt-drag-mode .track-offset-indicator {
        opacity: 1;
      }

      .track-offset-indicator::after {
        content: 'Alt + 드래그';
        display: block;
        font-size: 12px;
        font-weight: normal;
        opacity: 0.8;
        text-align: center;
        margin-top: 4px;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div
      style={{
        marginBottom: "20px",
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        paddingLeft: "24px",
        border: "1px solid red",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "sticky",
          left: "0px",
          width: "1000px",
        }}
      >
        <div>
          <input
            type="range"
            min="-1"
            max="1"
            step="0.01"
            defaultValue="0"
            onChange={(e) => {
              gainNode.gain.value = Math.pow(10, Number(e.target.value)); // 로그 스케일로 변환
            }}
          />
        </div>
        <div className="controls-container">
          <div className="transport-controls">
            <button
              onClick={isPlaying ? onPause : onPlay}
              className="btn btn-play"
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
            <button onClick={onStop} className="btn btn-stop">
              <StopCircle size={24} />
            </button>
            <button
              onClick={handleMute}
              disabled={!selection.start || !selection.end}
              className="btn btn-edit"
              title="Mute Selection"
            >
              <VolumeX size={24} />
            </button>
            <button
              onClick={handleDelete}
              disabled={!selection.start || !selection.end}
              className="btn btn-edit"
            >
              <Trash2 size={24} />
            </button>
            <button
              onClick={handleUndo}
              className="btn btn-edit"
              disabled={!historyState.canUndo} // false에서 수정
            >
              <RotateCcw size={24} />
            </button>
            <button
              onClick={handleRedo}
              className="btn btn-edit"
              disabled={!historyState.canRedo} // false에서 수정
            >
              <RotateCw size={24} />
            </button>
          </div>
          <div className="edit-controls"></div>
          <div className="time-display">
            <div className="time-value">
              {formatTime(currentTime)} /{" "}
              {formatTime(audioBuffer?.duration || 0)}
            </div>
            {selection.start !== null && selection.end !== null && (
              <div className="selection-display">
                Selection:{" "}
                {formatTime(Math.min(selection.start, selection.end))} -
                {formatTime(Math.max(selection.start, selection.end))}
              </div>
            )}
            <div
              className="offset-display"
              style={{ color: "white", fontFamily: "monospace" }}
            >
              Offset: {timeOffset > 0 ? "+" : ""}
              {(timeOffset * 1000).toFixed(0)}ms
            </div>
          </div>
        </div>
      </div>
      <div
        className="editor-container"
        ref={containerRef}
        style={{ flex: 1, border: "1px solid gray" }}
      >
        <div
          className={getTimelineClassName()}
          ref={timelineRef}
          onClick={handleTimelineClick} // 클릭 이벤트 추가
        >
          {audioBuffer &&
            generateTimeMarkers().map(({ time, position }) => (
              <div
                key={time}
                className="time-marker"
                style={{ left: `${position}%` }}
              >
                <div className="marker-line"></div>
                <div className="marker-time">{Math.floor(time)}s</div>
              </div>
            ))}
          <div
            className="timeline-playhead"
            style={{
              left: audioBuffer
                ? `${(currentTime / audioBuffer.duration) * 100}%`
                : 0,
            }}
            onMouseDown={handlePlayheadMouseDown}
          />
        </div>
        <div className="waveform-container">
          <canvas
            ref={canvasRef}
            className="waveform-canvas"
            width={800}
            height={100}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onClick={handleCanvasClick}
          />
          <div
            ref={playheadRef}
            className="playhead"
            style={{
              transform: `translateX(${
                audioBuffer
                  ? (currentTime / audioBuffer.duration) * canvasWidth
                  : 0
              }px)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SingleAudioEditor;
